/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars, no-nested-ternary */
import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Typography,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import { Plus, MoreVertical, Copy, Trash2, Bug } from 'lucide-react';
import { useNotify } from 'react-admin';
import type { GenModelPreset } from '@musedrops/shared';
import { supabase } from '../../../services/supabase';

/**
 * A preset may only be blocked from deletion by an active prompt configuration
 * that points at it — gen_prompts.model_preset and .model_preset_debug, both
 * FK-enforced with ON DELETE RESTRICT. The `*_preset_used` columns on shows /
 * stories / story_translations merely record which preset generated a past
 * artifact (historical provenance, no live dependency); their FKs were dropped
 * in 20260715000000, so they neither block deletion nor need checking here.
 */
const PRESET_USAGE_CHECKS: Array<{
  table: string;
  column: string;
  label: (count: number) => string;
}> = [
  { table: 'gen_prompts', column: 'model_preset', label: n => `${n} prompt(s)` },
  {
    table: 'gen_prompts',
    column: 'model_preset_debug',
    label: n => `${n} debug prompt(s)`,
  },
];

interface ModelPresetsListProps {
  presets: GenModelPreset[];
  selectedPreset: GenModelPreset | null;
  onSelect: (preset: GenModelPreset | null) => void;
  onCreate: () => void;
  onDelete: (presetId: string) => Promise<void>;
  onDuplicate: (preset: GenModelPreset) => void;
  loading: boolean;
}

export const ModelPresetsList: React.FC<ModelPresetsListProps> = ({
  presets,
  selectedPreset,
  onSelect,
  onCreate,
  onDelete,
  onDuplicate,
  loading,
}) => {
  const notify = useNotify();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPreset, setMenuPreset] = useState<GenModelPreset | null>(null);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    preset: GenModelPreset
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuPreset(preset);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPreset(null);
  };

  const handleDelete = async () => {
    if (menuPreset) {
      try {
        // Check if preset is referenced anywhere it can't be deleted from under
        const counts = await Promise.all(
          PRESET_USAGE_CHECKS.map(async ({ table, column }) => {
            const { count, error } = await supabase
              .from(table)
              .select('id', { count: 'exact', head: true })
              .eq(column, menuPreset.key);
            if (error) throw error;
            return count ?? 0;
          })
        );

        const usages = PRESET_USAGE_CHECKS.map((check, i) => {
          const count = counts[i] ?? 0;
          return count > 0 ? check.label(count) : null;
        }).filter((text): text is string => text !== null);

        if (usages.length > 0) {
          notify(
            `Cannot delete "${menuPreset.name}". It is used by ${usages.join(', ')}.`,
            { type: 'error' }
          );
          handleMenuClose();
          return;
        }

        if (
          window.confirm(
            `Are you sure you want to delete "${menuPreset.name}"?`
          )
        ) {
          try {
            await onDelete(menuPreset.id);
          } catch (error) {
            notify(
              error instanceof Error
                ? `Failed to delete preset: ${error.message}`
                : 'Failed to delete preset',
              { type: 'error' }
            );
          }
        }
      } catch {
        notify('Failed to check preset usage', { type: 'error' });
      }
    }
    handleMenuClose();
  };

  const handleDuplicate = () => {
    if (menuPreset) {
      onDuplicate(menuPreset);
    }
    handleMenuClose();
  };

  // Group presets by type
  const groupedPresets = presets.reduce(
    (acc, preset) => {
      if (!acc[preset.type]) {
        acc[preset.type] = [];
      }
      acc[preset.type].push(preset);
      return acc;
    },
    {} as Record<string, GenModelPreset[]>
  );

  const typeOrder = ['text', 'voice', 'image', 'music'];
  const sortedTypes = Object.keys(groupedPresets).sort(
    (a, b) => typeOrder.indexOf(a) - typeOrder.indexOf(b)
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with create button */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Presets</Typography>
        <IconButton onClick={onCreate} color="primary" size="small">
          <Plus />
        </IconButton>
      </Box>

      {/* Presets list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : presets.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2, textAlign: 'center' }}
          >
            No presets yet
          </Typography>
        ) : (
          <List dense sx={{ p: 0 }}>
            {sortedTypes.map(type => (
              <React.Fragment key={type}>
                <ListItem sx={{ px: 2, py: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                  >
                    {type}
                  </Typography>
                </ListItem>
                {groupedPresets[type].map(preset => (
                  <ListItem
                    key={preset.id}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={e => handleMenuOpen(e, preset)}
                        sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                        className="preset-menu-button"
                      >
                        <MoreVertical size={16} />
                      </IconButton>
                    }
                    sx={{
                      '&:hover .preset-menu-button': {
                        opacity: 1,
                      },
                    }}
                  >
                    <ListItemButton
                      selected={selectedPreset?.id === preset.id}
                      onClick={() => onSelect(preset)}
                    >
                      <ListItemText
                        primary={
                          <Box
                            component="span"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            {preset.name}
                            {preset.isDebug && (
                              <Bug
                                size={14}
                                color="#f44336"
                                style={{ flexShrink: 0 }}
                                aria-label="Debug preset"
                              />
                            )}
                          </Box>
                        }
                        primaryTypographyProps={{
                          variant: 'body2',
                          noWrap: true,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                {type !== sortedTypes[sortedTypes.length - 1] && (
                  <Divider sx={{ my: 1 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Context menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDuplicate}>
          <Copy size={16} style={{ marginRight: 8 }} />
          Duplicate
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <Trash2 size={16} style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};
